import React, {useState} from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Mail, ShieldCheck } from "lucide-react";
import { getPendingApprovals } from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { EmptyState, ErrorState, LoadingCards, PageHeader } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { fiRelative } from "@/lib/constants";

const TYPE_META = {
  email: { label: "Sähköposti", icon: Mail },
  calendar: { label: "Kalenteri", icon: Calendar },
};

export default function Approvals() {
  const [offset,setOffset]=useState(0);
  const { data, loading, error, refetch, refreshing } = useData(()=>getPendingApprovals(offset), [offset], ["approvals"],{autoRefreshMs:30000});
  const items = data?.items || [];

  return (
    <div>
      <PageHeader icon={ShieldCheck} title="Hyväksynnät" subtitle="Tallennetut sähköposti- ja kalenteriluonnokset." />
      <div className="mb-4 rounded-2xl border border-primary/25 bg-primary/5 p-4">
        <p className="text-sm font-semibold text-foreground">{loading ? "Tarkistetaan…" : error ? "Hyväksyntöjen määrää ei saatu" : `Tallennettuja luonnoksia: ${data.count}`}</p>
        <p className="mt-1 text-xs text-muted-foreground">Tämä näkymä ei hyväksy eikä suorita mitään automaattisesti. Toiminto vahvistetaan aina sen omassa näkymässä.</p>
        <p className="mt-2 text-xs text-muted-foreground">Keskustelun tallentamattomat toimintoehdotukset eivät kuulu tähän listaan. Yhteistyöpyynnöt tarkistetaan Yhteistyö-näkymässä.</p>
      </div>

      {error&&data&&<div role="alert" className="mb-3 text-sm text-amber-400">Päivitys epäonnistui. Alla näkyvät aiemmin haetut luonnokset. <button type="button" className="min-h-11 underline" onClick={refetch}>Yritä uudelleen</button></div>}
      {loading ? <LoadingCards /> : error&&!data ? <ErrorState onRetry={refetch} /> : items.length === 0 ? !error&&(
        <EmptyState icon={ShieldCheck} title={data.count ? "Tällä sivulla ei ole luonnoksia" : "Ei tallennettuja hyväksyntää odottavia luonnoksia"} hint="Tämä koskee vain sähköposti- ja kalenteriluonnoksia, ei avoimia keskusteluehdotuksia tai yhteistyöpyyntöjä." />
      ) : (
        <div className="space-y-3" data-testid="approval-queue">
          {items.map((item) => {
            const meta = TYPE_META[item.type] || TYPE_META.calendar;
            const Icon = meta.icon;
            return (
              <article key={item.id} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div><p className="text-[10px] font-semibold uppercase tracking-wide text-primary">{meta.label}</p><h2 className="font-medium text-foreground">{item.title}</h2></div>
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{item.detail}</p>
                    {item.created_at && <p className="mt-1 text-[11px] text-muted-foreground">Luotu {fiRelative(item.created_at)}</p>}
                    <Link to={item.target_path} className="mt-3 inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-primary">Tarkista {meta.label.toLocaleLowerCase("fi-FI")} <ChevronRight className="h-3.5 w-3.5" /></Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {data && !error && <div className="mt-4 space-y-2">
        <p className="text-xs text-muted-foreground">Näytetään {items.length ? `${offset+1}–${offset+items.length}` : "0"} / {data.count}. Sähköpostiluonnokset ensin, sitten kalenteriluonnokset.</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!offset || loading || refreshing} onClick={()=>setOffset(Math.max(0,offset-40))} className="min-h-11 rounded-full border border-border px-4 text-sm disabled:opacity-50">Edellinen sivu</button>
          <button type="button" disabled={!offset || loading || refreshing} onClick={()=>setOffset(0)} className="min-h-11 rounded-full border border-border px-4 text-sm disabled:opacity-50">Alkuun</button>
          <button type="button" disabled={!data.has_more || loading || refreshing} onClick={()=>setOffset(offset+40)} className="min-h-11 rounded-full border border-border px-4 text-sm disabled:opacity-50">Seuraava sivu</button>
          <button type="button" disabled={loading || refreshing} onClick={refetch} className="min-h-11 rounded-full border border-border px-4 text-sm disabled:opacity-50">Päivitä lista</button>
        </div>
      </div>}
    </div>
  );
}
